/**
 * Host-scoped default locale redirect for the international storefront domain.
 *
 * When a request hits a configured intl host without a locale alias in the path
 * (e.g. / or /g/shirts), redirect to /{alias}/... so UK bare / stays en-GB on
 * sit/uk domains while intl defaults to AU (or another configured alias).
 *
 * Mount this middleware AFTER explicit SSR/API routes (process-login, /api/*, etc.)
 * and BEFORE the page render catch-alls. Only GET/HEAD are redirected — POSTs to
 * bare endpoints are never rewritten.
 */

// Used when BM intlDefaultLocaleHosts hasn't been pushed to MRT yet.
export const DEFAULT_INTL_REDIRECT_HOSTS = ['intl.garageclothing.xyz']
export const DEFAULT_INTL_LOCALE_ALIAS = 'au'

/**
 * @param {string} [host]
 * @returns {string}
 */
export const normalizeHostname = (host = '') => String(host).split(':')[0].trim().toLowerCase()

/**
 * Public hostname as the browser sees it (eCDN/Cloudflare may set forwarded headers
 * while Express `Host` is the mobify origin).
 * @param {{get?: function(string): string, hostname?: string, headers?: Record<string, string|string[]>}} [req]
 * @returns {string}
 */
export const resolveRequestHostname = (req = {}) => {
    const header = (name) => {
        const value = req.get?.(name) ?? req.headers?.[name.toLowerCase()]
        if (Array.isArray(value)) {
            return value[0]
        }
        return value
    }

    const forwarded =
        header('x-forwarded-host') || header('x-original-host') || header('host') || req.hostname || ''

    // x-forwarded-host can be a comma-separated list; leftmost is the original client host
    return normalizeHostname(String(forwarded).split(',')[0])
}

/**
 * @param {string|null|undefined} value
 * @param {string[]} [fallback]
 * @returns {string[]}
 */
export const parseHostList = (value, fallback = DEFAULT_INTL_REDIRECT_HOSTS) => {
    if (value == null || value === '') {
        return [...fallback]
    }

    const normalized = String(value).trim().toLowerCase()
    if (normalized === '0' || normalized === 'false' || normalized === 'off') {
        return []
    }

    return normalized
        .split(',')
        .map((entry) => normalizeHostname(entry))
        .filter(Boolean)
}

/**
 * @param {Array<{alias?: string, id?: string}>} [locales]
 * @returns {Set<string>}
 */
export const getLocaleAliases = (locales = []) =>
    new Set(
        locales
            .flatMap((locale) => [locale?.alias, locale?.id])
            .filter(Boolean)
            .map((value) => String(value).toLowerCase())
    )

/**
 * @param {{
 *  hostname?: string,
 *  path?: string,
 *  url?: string,
 *  intlHosts?: string[],
 *  defaultAlias?: string,
 *  localeAliases?: Set<string>
 * }} options
 * @returns {string|null} Relative redirect target, or null when no redirect
 */
export const buildIntlDefaultLocaleRedirect = ({
    hostname = '',
    path = '/',
    url = '',
    intlHosts = DEFAULT_INTL_REDIRECT_HOSTS,
    defaultAlias = DEFAULT_INTL_LOCALE_ALIAS,
    localeAliases = new Set()
} = {}) => {
    const host = normalizeHostname(hostname)
    if (!intlHosts?.length || !host || !intlHosts.includes(host)) {
        return null
    }

    const pathname = String(path || '/').split('?')[0] || '/'

    const firstSegment = pathname.split('/').filter(Boolean)[0]?.toLowerCase()
    if (firstSegment && localeAliases.has(firstSegment)) {
        return null
    }

    const alias = String(defaultAlias || DEFAULT_INTL_LOCALE_ALIAS)
        .replace(/^\/+|\/+$/g, '')
        .toLowerCase()
    if (!alias) {
        return null
    }

    const remainder = pathname === '/' ? '' : pathname
    const targetPath = `/${alias}${remainder}`.replace(/\/{2,}/g, '/')
    const queryIndex = String(url).indexOf('?')
    const query = queryIndex >= 0 ? String(url).slice(queryIndex) : ''

    return `${targetPath}${query}`
}

/**
 * Express middleware factory.
 * @param {{
 *  intlHosts?: string[],
 *  defaultAlias?: string,
 *  localeAliases?: Set<string>
 * }} options
 * @returns {function}
 */
export const createIntlDefaultLocaleRedirectMiddleware = ({
    intlHosts = DEFAULT_INTL_REDIRECT_HOSTS,
    defaultAlias = DEFAULT_INTL_LOCALE_ALIAS,
    localeAliases = new Set()
} = {}) => {
    return (req, res, next) => {
        // Page navigations only — leave POST /process-login etc. alone even if mount order slips
        const method = String(req.method || 'GET').toUpperCase()
        if (method !== 'GET' && method !== 'HEAD') {
            return next()
        }

        const destination = buildIntlDefaultLocaleRedirect({
            hostname: resolveRequestHostname(req),
            path: req.path,
            url: req.url,
            intlHosts,
            defaultAlias,
            localeAliases
        })

        if (destination) {
            return res.redirect(302, destination)
        }

        return next()
    }
}
